import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";

// Custom Virtual DataGrid
import { VirtualDataGrid } from "./VirtualDataGrid";

// Components
import CategoryModal from "./CategoryModal";
import RoomsSingle from "./RoomsSingle";
import RoomsSingleTimeline from "./RoomsSingleTimeline";
import RoomsMapView from "./RoomsMapView";

// Contexts and hooks
import PropertyContext from "../contexts/PropertyContext";
import { useOptimizedRoomFiltering } from "../hooks/useOptimizedRoomFiltering";
import { useRoomData } from "../hooks/useRoomData";
import { useRoomCalculations } from "../hooks/useRoomCalculations";

// Utils and constants
import {
  ROOM_FILTER_TYPES,
  STATUS_OPTIONS,
  HOUSEKEEPING_OPTIONS,
  MAINTENANCE_OPTIONS,
} from "../constants/roomConstants";

// Report generators
import GenerateDRListPDF from "./GenerateDRListPDF";
import GenerateVendorBreakdownPDF from "./reports/GenerateVendorBreakdownPDF";
import GenerateOpenRoomIssuesPDF from "./reports/GenerateOpenRoomIssuesPDF";

// Memoized components for optimal performance
const MemoizedSelect = React.memo(
  ({ value, onChange, options, size = "small" }) => (
    <Select
      value={value || ""}
      onChange={onChange}
      size={size}
      sx={{ width: "100%" }}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  )
);

// Editable notes cell - keeps a local value while typing and persists on blur
const MemoizedNotesField = React.memo(({ value, onSave }) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  return (
    <TextField
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => {
        if (localValue !== (value || "")) {
          onSave(localValue);
        }
      }}
      size="small"
      multiline
      maxRows={3}
      placeholder="Add note..."
      sx={{ width: "100%" }}
    />
  );
});

const SuperOptimizedRooms2 = React.memo(({ user }) => {
  const { rooms, items, selectedProperty, selectedClient, itemCategories } = useContext(PropertyContext);

  // Custom hooks for data management
  const { itemsByCategory, loading, handleEdit, uniqueCategories } =
    useRoomData(items, selectedClient, selectedProperty, itemCategories, user);

  // No pagination needed - handle all data at once with virtual scrolling
  const {
    filteredRooms: allFilteredRooms,
    selectionOption,
    searchTerm,
    handleFilterChange,
    handleSearchChange,
    filterGroups,
  } = useOptimizedRoomFiltering(rooms, 10000); // Very large page size

  // High-performance calculations
  const { getDifficulty, getCost } = useRoomCalculations(rooms, items);

  // Local state for UI components
  const [viewMode, setViewMode] = useState(0); // 0 = list, 1 = map
  const [singleRoom, setSingleRoom] = useState(null);
  const [singleRoomTimeline, setSingleRoomTimeline] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [statusMessage, setStatusMessage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [sortModel, setSortModel] = useState([]);

  // Performance monitoring
  const [performanceMetrics, setPerformanceMetrics] = useState({
    renderTime: 0,
    roomCount: 0,
    lastUpdate: Date.now(),
  });

  // Monitor performance
  useEffect(() => {
    const startTime = performance.now();
    const timer = setTimeout(() => {
      const endTime = performance.now();
      setPerformanceMetrics((prev) => ({
        renderTime: Math.round(endTime - startTime),
        roomCount: allFilteredRooms.length,
        lastUpdate: Date.now(),
      }));
    }, 0);

    return () => clearTimeout(timer);
  }, [allFilteredRooms]);

  // Memoized callbacks
  const handleSetSingleRoom = useCallback((room) => {
    setSingleRoom(room);
  }, []);

  const handleRoomChange = useCallback(
    (e) => {
      handleFilterChange(e.target.value);
    },
    [handleFilterChange]
  );

  const handleSearchInputChange = useCallback(
    (e) => {
      handleSearchChange(e.target.value);
    },
    [handleSearchChange]
  );

  const handleOpenModal = useCallback((currentRoom, category) => {
    setModalOpen(true);
    setSelectedItemId(currentRoom);
    setSelectedCategory(category);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedItemId(null);
    setSelectedCategory("");
  }, []);

  // Row selection handler for virtual grid
  const handleRowSelectionChange = useCallback(
    (selectedRowIds) => {
      const selectedRooms = selectedRowIds
        .map((roomName) =>
          allFilteredRooms.find((room) => room.displayName === roomName)
        )
        .filter(Boolean);
      setSelectedRows(selectedRooms);
    },
    [allFilteredRooms]
  );

  // Handle row click
  const handleRowClick = useCallback(
    (row) => {
      handleSetSingleRoom(row);
    },
    [handleSetSingleRoom]
  );

  // Handle sort model change
  const handleSortModelChange = useCallback((newSortModel) => {
    setSortModel(newSortModel);
  }, []);

  // Helper to get status options - includes Facilities only if room already has that status
  const getStatusOptions = useCallback((currentStatus) => {
    const baseOptions = [...STATUS_OPTIONS];
    const statusLower = (currentStatus || '').toLowerCase();

    if (statusLower === 'facilities' && !baseOptions.some(opt => opt.toLowerCase() === 'facilities')) {
      baseOptions.push('Facilities');
    }

    return baseOptions;
  }, []);

  // Create optimized columns with virtual DataGrid compatibility
  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "displayName",
        headerName: "Room",
        width: 100,
        sortable: true,
        renderCell: ({ value, row }) => (
          <div
            style={{
              cursor: "pointer",
              fontWeight: "bold",
              color: "#1976d2",
            }}
          >
            {value}
          </div>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        sortable: true,
        disablePadding: true,
        renderCell: ({ value, row }) => {
          const handleChange = (event) => {
            handleEdit(row.displayName, "headerdata", {
              status: event.target.value,
            });
          };

          return (
            <MemoizedSelect
              value={value}
              onChange={handleChange}
              options={getStatusOptions(value)}
            />
          );
        },
      },
      {
        field: "selectedHousekeepingRequirements",
        headerName: "Housekeeping",
        width: 130,
        sortable: true,
        disablePadding: true,
        renderCell: ({ value, row }) => {
          const handleChange = (event) => {
            handleEdit(row.displayName, "headerdata", {
              selectedHousekeepingRequirements: event.target.value,
            });
          };

          return (
            <MemoizedSelect
              value={value}
              onChange={handleChange}
              options={HOUSEKEEPING_OPTIONS}
            />
          );
        },
      },
      {
        field: "selectedMaintenanceRequirements",
        headerName: "Maintenance",
        width: 130,
        sortable: true,
        disablePadding: true,
        renderCell: ({ value, row }) => {
          const handleChange = (event) => {
            handleEdit(row.displayName, "headerdata", {
              selectedMaintenanceRequirements: event.target.value,
            });
          };

          return (
            <MemoizedSelect
              value={value}
              onChange={handleChange}
              options={MAINTENANCE_OPTIONS}
            />
          );
        },
      },
      {
        field: "difficulty",
        headerName: "Difficulty",
        width: 120,
        sortable: true,
        type: "number",
        renderCell: ({ row }) => {
          const score = getDifficulty(row.displayName);
          return (
            <Chip
              label={score}
              size="small"
              color={score > 5 ? "error" : score > 2 ? "warning" : "success"}
            />
          );
        },
        valueGetter: ({ row }) => getDifficulty(row.displayName),
      },
      {
        field: "cost",
        headerName: "Cost",
        width: 100,
        sortable: true,
        type: "number",
        renderCell: ({ row }) => {
          const cost = getCost(row.displayName);
          return `$${cost.toFixed(2)}`;
        },
        valueGetter: ({ row }) => getCost(row.displayName),
      },
    ];

    // Room notes column (replaces the per-category columns)
    const notesColumn = {
      field: "unitNotes",
      headerName: "Notes",
      flex: 1,
      minWidth: 250,
      sortable: false,
      disablePadding: true,
      renderCell: ({ row }) => (
        <MemoizedNotesField
          value={row.unitNotes}
          onSave={(newValue) =>
            handleEdit(row.displayName, "headerdata", { unitNotes: newValue })
          }
        />
      ),
    };

    return [...baseColumns, notesColumn];
  }, [handleEdit, getDifficulty, getCost, getStatusOptions]);

  // PDF generation handlers (unchanged)
  const handleGenerateDRListPDF = useCallback(
    async (rooms, fileName) => {
      if (!rooms?.length) return;

      setStatusMessage("Generating DRL PDF...");
      setIsGenerating(true);
      try {
        await GenerateDRListPDF(
          selectedClient,
          selectedProperty,
          rooms,
          setStatusMessage,
          user,
          fileName
        );
      } catch (error) {
        console.error("Error generating DRL PDF:", error);
        setStatusMessage("Error generating PDF");
      } finally {
        setIsGenerating(false);
      }
    },
    [selectedClient, selectedProperty, user]
  );

  const handleGenerateVendorBreakdown = useCallback(
    async (rooms, fileName) => {
      if (!rooms?.length) return;

      setStatusMessage("Generating Vendor Breakdown PDF...");
      setIsGenerating(true);
      try {
        await GenerateVendorBreakdownPDF(
          selectedProperty,
          rooms,
          setStatusMessage,
          user,
          fileName,
          items
        );
      } catch (error) {
        console.error("Error generating vendor breakdown PDF:", error);
        setStatusMessage("Error generating PDF");
      } finally {
        setIsGenerating(false);
      }
    },
    [selectedProperty, user, items]
  );

  const handleGenerateOpenRoomIssues = useCallback(
    async (rooms, fileName) => {
      if (!rooms?.length) return;

      setStatusMessage("Generating Open Room Issues PDF...");
      setIsGenerating(true);
      try {
        await GenerateOpenRoomIssuesPDF(
          selectedClient,
          selectedProperty,
          rooms,
          setStatusMessage,
          user,
          fileName,
          items
        );
      } catch (error) {
        console.error("Error generating open room issues PDF:", error);
        setStatusMessage("Error generating PDF");
      } finally {
        setIsGenerating(false);
      }
    },
    [selectedClient, selectedProperty, user, items]
  );

  const promptAndGenerate = useCallback(
    (handler) => {
      const fileName = prompt("Enter file name");
      if (fileName) {
        handler(selectedRows, fileName);
      }
    },
    [selectedRows]
  );

  // Early returns
  if (!user?.fullName) {
    return (
      <Typography color="error">User information not available</Typography>
    );
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading room data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "99%" }}>
      {singleRoomTimeline && (
        <RoomsSingleTimeline
          singleRoom={singleRoom}
          setSingleRoomTimeline={setSingleRoomTimeline}
        />
      )}

      {singleRoom && !singleRoomTimeline && (
        <RoomsSingle
          singleRoom={singleRoom}
          setSingleRoom={setSingleRoom}
          itemsByCategory={itemsByCategory}
          handleEdit={handleEdit}
          handleOpenModal={handleOpenModal}
          statusOptions={STATUS_OPTIONS}
          hKOptions={HOUSEKEEPING_OPTIONS}
          mTOptions={MAINTENANCE_OPTIONS}
          uniqueCategories={uniqueCategories}
          setSingleRoomTimeline={setSingleRoomTimeline}
          user={user}
        />
      )}

      <Box sx={{ display: singleRoom ? "none" : "block" }}>
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="List View" />
          <Tab label="Map View" />
        </Tabs>

        {viewMode === 1 && <RoomsMapView onRoomClick={handleSetSingleRoom} />}

        {viewMode === 0 && (
          <>
            {/* Performance Metrics Card */}
        {process.env.NODE_ENV === "development" && (
          <Card
            sx={{
              mb: 2,
              bgcolor: "success.light",
              color: "success.contrastText",
            }}
          >
            <CardContent sx={{ py: 1 }}>
              <Box display="flex" gap={2} alignItems="center">
                <Chip
                  label={`🚀 Virtual DataGrid: ${allFilteredRooms.length} rooms`}
                  size="small"
                  color="primary"
                />
                <Chip
                  label={`Render: ${performanceMetrics.renderTime}ms`}
                  size="small"
                  color={
                    performanceMetrics.renderTime > 50 ? "error" : "success"
                  }
                />
                <Chip
                  label={`Categories: ${Math.min(uniqueCategories.length, 6)}/${
                    uniqueCategories.length
                  }`}
                  size="small"
                  variant="outlined"
                />
                <Typography variant="caption">
                  ⚡ No pagination needed - All {allFilteredRooms.length} rooms
                  virtualized
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Filter Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="room-types-label">Room Types</InputLabel>
            <Select
              labelId="room-types-label"
              value={selectionOption}
              label="Room Types"
              onChange={handleRoomChange}
            >
              <MenuItem value={ROOM_FILTER_TYPES.ALL}>
                All Rooms ({filterGroups.all || 0})
              </MenuItem>
              <MenuItem value={ROOM_FILTER_TYPES.AVAILABLE}>
                Available Rooms ({filterGroups.available || 0})
              </MenuItem>
              <MenuItem value={ROOM_FILTER_TYPES.DOWN}>
                Down Rooms ({filterGroups.down || 0})
              </MenuItem>
              <MenuItem value={ROOM_FILTER_TYPES.VENDOR_NEEDED}>
                Vendor Needed ({filterGroups.vendorNeeded || 0})
              </MenuItem>
              <MenuItem value={ROOM_FILTER_TYPES.TURNABLE}>
                Turnable Rooms ({filterGroups.turnable || 0})
              </MenuItem>
              <MenuItem value={ROOM_FILTER_TYPES.OCCUPIED}>
                Occupied Rooms ({filterGroups.occupied || 0})
              </MenuItem>
              <MenuItem value={ROOM_FILTER_TYPES.VACANT}>
                Vacant Rooms ({filterGroups.vacant || 0})
              </MenuItem>
            </Select>
            <FormHelperText>
              Showing all {allFilteredRooms.length} rooms (no pagination)
            </FormHelperText>
          </FormControl>

          <TextField
            label="Search Rooms"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchInputChange}
            placeholder="Type at least 3 characters..."
            sx={{ minWidth: 250 }}
          />

          {selectedRows.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" color="primary">
                {selectedRows.length} selected
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={isGenerating}
                onClick={() => promptAndGenerate(handleGenerateDRListPDF)}
              >
                Generate DRL
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={isGenerating}
                onClick={() => promptAndGenerate(handleGenerateVendorBreakdown)}
              >
                Generate Order Sheet
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={isGenerating}
                onClick={() => promptAndGenerate(handleGenerateOpenRoomIssues)}
              >
                Generate Room Issues
              </Button>
            </Box>
          )}
        </Box>

        {statusMessage && (
          <Typography variant="body2" color="info" sx={{ mb: 1 }}>
            {statusMessage}
          </Typography>
        )}

        {/* Virtual DataGrid - No pagination needed! */}
        {allFilteredRooms.length > 0 ? (
          <VirtualDataGrid
            data={allFilteredRooms}
            columns={columns}
            height={700}
            rowHeight={52}
            checkboxSelection
            onRowClick={handleRowClick}
            onRowSelectionChange={handleRowSelectionChange}
            getRowId={(row) => row.displayName}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            density="compact"
            loading={loading}
            noRowsText="No rooms found matching the current filter criteria."
          />
        ) : (
          <Typography variant="body1" color="text.secondary">
            No rooms found matching the current filter criteria.
          </Typography>
        )}
          </>
        )}
      </Box>

      {modalOpen && (
        <CategoryModal
          open={modalOpen}
          onClose={handleCloseModal}
          category={selectedCategory}
          room={selectedItemId}
          itemsByCategory={itemsByCategory}
        />
      )}
    </Box>
  );
});

SuperOptimizedRooms2.displayName = "SuperOptimizedRooms2";

export default SuperOptimizedRooms2;
